"use client";

import { useLayoutEffect, useMemo, useRef } from "react";
import { useSessionBootstrap } from "../hooks/useSessionBootstrap";
import { usePathname } from "next/navigation";
import { RiShieldCheckLine } from "react-icons/ri";
import { Badge } from "@dionatanbig2be/big2be-ui";

import { ROUTES, isAppPagePath, isClubPath } from "@/shared/routes";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const BARE_ROUTES = new Set<string>([ROUTES.error]);

/**
 * Rotas de VITRINE — as que exibem a barra de categorias. É a navegação de
 * loja: home, categoria, busca, produto, receitas e as páginas do portal. Ficam de fora as telas onde a barra
 * só atrapalharia (checkout, conta, páginas institucionais) e a casca `bare`.
 */
const CATEGORY_BAR_ROUTES: readonly string[] = [ROUTES.category, ROUTES.search, ROUTES.product, ROUTES.recipes];

function showsCategoryBar(pathname: string): boolean {
    return (
        pathname === ROUTES.home ||
        CATEGORY_BAR_ROUTES.some((r) => pathname.startsWith(r)) ||
        isAppPagePath(pathname)
    );
}

// `overscroll-y-none`: o scroller interno não faz rubber-band no iOS. Com o
// bounce, a barra de categorias (que mora DENTRO do scroller) descia junto do
// conteúdo e abria uma faixa branca entre a busca e o menu — Android não tem
// bounce, por isso só aparecia no iPhone.
const CONTAINER_CLASS_STANDARD = "flex-1 overflow-x-hidden overflow-y-auto overscroll-y-none";
const CONTAINER_CLASS_SIMPLIFIED = "flex-1 overflow-x-hidden overflow-y-auto overscroll-y-none flex flex-col";

function SecurityBadgeComponent() {
    return (
        <Badge
            size="lg"
            // O selo vive DENTRO do header: herda a cor de texto de lá
            // (`--color-primary-content` do tema do cliente). Com `text-white`
            // fixo ele sumia em todo tenant de header claro.
            className="inline-flex items-center gap-2 whitespace-nowrap border-transparent bg-transparent text-sm font-semibold text-current"
        >
            <RiShieldCheckLine size={17} aria-hidden="true" />
            Compra 100% segura
        </Badge>
    );
}

/** Instância única: mantém `simplifiedSlot` referencialmente estável entre renders. */
const SECURITY_BADGE_SLOT = <SecurityBadgeComponent />;

function useAppShellController() {
    const pathname = usePathname();

    return useMemo(() => {
        const isBare = BARE_ROUTES.has(pathname);
        const isSimplified = !isBare && pathname.startsWith(ROUTES.checkout);

        return {
            pathname,
            isBare,
            isSimplified,
            // O Clube tem o cabeçalho dele, montado pelo layout da seção (modules/club).
            withStoreHeader: !isClubPath(pathname),
            withCategoryBar: !isBare && !isSimplified && showsCategoryBar(pathname),
            containerClassName: isSimplified
                ? CONTAINER_CLASS_SIMPLIFIED
                : CONTAINER_CLASS_STANDARD,
            headerSlot: isSimplified ? SECURITY_BADGE_SLOT : undefined,
        };
    }, [pathname]);
}

/**
 * `categoryBar` é um Server Component recebido PRONTO do root layout (não um
 * componente montado aqui): o HTML/RSC dele vem resolvido do servidor e o
 * AppShell só decide onde e quando exibir. Assim a barra não vira trabalho de
 * cliente e, por viver no layout, sobrevive à troca de rota sem remontar.
 */
export function AppShell({
    children,
    categoryBar,
    categoryBarPosition = "below",
}: {
    children: React.ReactNode;
    categoryBar?: React.ReactNode;
    /** Onde a barra de categorias fica em relação ao cabeçalho — configurado na Identidade Visual. */
    categoryBarPosition?: "above" | "below" | "hidden";
}) {
    const { pathname, isBare, isSimplified, withStoreHeader, withCategoryBar, containerClassName, headerSlot } =
        useAppShellController();
    const scrollerRef = useRef<HTMLDivElement>(null);

    // Carrinho + favoritos numa chamada, assim que sessão e loja resolvem.
    // Aqui em cima porque vale para toda tela, não só as que mostram carrinho.
    useSessionBootstrap();

    // Quem rola a loja é o container abaixo, não a janela (o body é travado no
    // root layout). Ele vive no layout e atravessa a navegação com a rolagem da
    // página anterior — o reset do Next só zera a janela. Só a troca de CAMINHO
    // volta ao topo: filtros mudam a query com `scroll: false` de propósito, e
    // âncora (#) fica com o pulo do Next.
    useLayoutEffect(() => {
        if (!window.location.hash) scrollerRef.current?.scrollTo(0, 0);
    }, [pathname]);

    if (isBare) {
        return <>{children}</>;
    }

    const showBar = withCategoryBar && categoryBarPosition !== "hidden";

    return (
        <div className="flex h-screen flex-col overflow-x-clip bg-base-100 text-base-content supports-[height:100dvh]:h-dvh">
            {showBar && categoryBarPosition === "above" && categoryBar}
            {withStoreHeader && <Header simplified={isSimplified} simplifiedSlot={headerSlot} />}

            <div ref={scrollerRef} className={containerClassName}>
                {showBar && categoryBarPosition === "below" && categoryBar}
                {children}
                <Footer simplified={isSimplified} />
            </div>
        </div>
    );
}
